import type {
  ClientRecord,
  OsFamily,
  PluginParamType,
  PluginSpec,
  PluginView,
} from '@occ/contracts';
import { useCan } from '@refinedev/core';
import {
  ArrowDown,
  ArrowUp,
  ListChecks,
  MonitorCog,
  Pencil,
  Plus,
  Terminal,
  Trash2,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeHttpError } from '../../api/http';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  Modal,
  Notice,
  Select,
  Textarea,
  Toasts,
  useToasts,
} from '../../components/ui';
import { listClients } from '../../data/admin';
import {
  createPlugin,
  deletePlugin,
  listPluginEnablements,
  listPlugins,
  setPluginEnablement,
  updatePlugin,
} from '../../data/plugins';

/**
 * Administración de plugins (M6.1) — lo que lancelot pidió el 2026-08-14:
 * comandos repetidos, empaquetados con parámetros de entrada que llegan al
 * script como variables de entorno, habilitables por cliente.
 *
 * Crear o editar un plugin no es algo que un nodo ejecute, así que vive aquí
 * y no en el catálogo de acciones — mismo criterio que las reglas de alerta.
 * Correr uno ya habilitado sí pasa por el catálogo (`script.run` con
 * `libraryId`), desde la ficha del nodo.
 */

type ParamForm = {
  name: string;
  label: string;
  type: PluginParamType;
  required: boolean;
  default: string;
};

type StepForm = {
  shell: 'powershell' | 'bash' | 'sh';
  script: string;
  timeoutSeconds: number;
  continueOnError: boolean;
};

type PluginForm = {
  id: string | null;
  name: string;
  description: string;
  osFamily: OsFamily | '';
  paramsSchema: ParamForm[];
  steps: StepForm[];
};

const emptyParam = (): ParamForm => ({
  name: '',
  label: '',
  type: 'string',
  required: false,
  default: '',
});
const emptyStep = (): StepForm => ({
  shell: 'bash',
  script: '',
  timeoutSeconds: 120,
  continueOnError: false,
});

const formOf = (plugin?: PluginView): PluginForm => ({
  id: plugin?.id ?? null,
  name: plugin?.name ?? '',
  description: plugin?.description ?? '',
  osFamily: plugin?.osFamily ?? '',
  paramsSchema:
    plugin?.paramsSchema.map((param) => ({
      name: param.name,
      label: param.label ?? '',
      type: param.type,
      required: param.required,
      default: param.default ?? '',
    })) ?? [],
  steps: plugin?.steps.map((step) => ({
    shell: step.shell,
    script: step.script,
    timeoutSeconds: step.timeoutSeconds,
    continueOnError: step.continueOnError,
  })) ?? [emptyStep()],
});

const toSpec = (form: PluginForm): PluginSpec => ({
  name: form.name.trim(),
  description: form.description.trim() || undefined,
  osFamily: form.osFamily || undefined,
  paramsSchema: form.paramsSchema
    .filter((param) => param.name.trim() !== '')
    .map((param) => ({
      name: param.name.trim(),
      label: param.label.trim() || undefined,
      type: param.type,
      required: param.required,
      default: param.required ? undefined : param.default.trim() || undefined,
    })),
  steps: form.steps.map((step) => ({
    kind: 'exec' as const,
    shell: step.shell,
    script: step.script,
    timeoutSeconds: step.timeoutSeconds,
    continueOnError: step.continueOnError,
  })),
});

const OS_LABEL: Record<OsFamily, string> = { windows: 'windows', linux: 'linux', darwin: 'darwin' };

export function PluginsPage() {
  const { data: canEdit } = useCan({ resource: 'plugins', action: 'edit' });

  const [plugins, setPlugins] = useState<PluginView[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [form, setForm] = useState<PluginForm | null>(null);
  const [deleting, setDeleting] = useState<PluginView | null>(null);
  const [managingClients, setManagingClients] = useState<PluginView | null>(null);
  const [enablements, setEnablements] = useState<Map<string, boolean> | null>(null);
  const [busy, setBusy] = useState(false);

  const toasts = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextPlugins, nextClients] = await Promise.all([listPlugins(), listClients()]);
      setPlugins(nextPlugins);
      setClients(nextClients);
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const spec = toSpec(form);
      if (form.id) {
        await updatePlugin(form.id, spec);
      } else {
        await createPlugin(spec);
      }
      setForm(null);
      toasts.ok(form.id ? 'Plugin actualizado.' : 'Plugin creado.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deletePlugin(deleting.id);
      setDeleting(null);
      toasts.ok('Plugin borrado.');
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const openClients = async (plugin: PluginView) => {
    setManagingClients(plugin);
    setEnablements(null);
    try {
      const rows = await listPluginEnablements(plugin.id);
      setEnablements(new Map(rows.map((row) => [row.clientId, row.enabled])));
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
      setManagingClients(null);
    }
  };

  const toggleClient = async (clientId: string, enabled: boolean) => {
    if (!managingClients) return;
    // Optimista: son toggles individuales y frecuentes, esperar cada uno haría
    // la pantalla sentirse más lenta que el propio backend.
    setEnablements((current) => new Map(current).set(clientId, enabled));
    try {
      await setPluginEnablement(managingClients.id, clientId, enabled);
    } catch (cause) {
      setEnablements((current) => new Map(current).set(clientId, !enabled));
      toasts.fail(describeHttpError(cause));
    }
  };

  const setAllClients = async (enabled: boolean) => {
    if (!managingClients || clients.length === 0) return;
    const previous = enablements;
    setEnablements(new Map(clients.map((client) => [client.id, enabled])));
    try {
      await Promise.all(
        clients.map((client) => setPluginEnablement(managingClients.id, client.id, enabled)),
      );
    } catch (cause) {
      setEnablements(previous);
      toasts.fail(describeHttpError(cause));
    }
  };

  const enabledCount = enablements ? [...enablements.values()].filter(Boolean).length : 0;

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Plugins</h1>
          <p className="text-sm opacity-60">
            Scripts reutilizables y parametrizados. Habilítalos por cliente antes de que aparezcan
            en la ficha de un nodo.
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!canEdit?.can}
          title={canEdit?.can ? undefined : canEdit?.reason}
          onClick={() => setForm(formOf())}
        >
          <Plus className="size-4" />
          Nuevo plugin
        </Button>
      </div>

      {!canEdit?.can && (
        <Alert>{canEdit?.reason ?? 'Solo un administrador del tenant administra plugins.'}</Alert>
      )}

      {error !== null && <ErrorNotice error={error} what="Cargar los plugins" />}

      {loading && plugins.length === 0 ? (
        <Loading label="Cargando plugins…" />
      ) : plugins.length === 0 ? (
        <Card>
          <EmptyState title="Ningún plugin todavía">
            Un plugin es una secuencia de comandos con parámetros de entrada, listos para correr
            contra cualquier nodo habilitado.
          </EmptyState>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plugins.map((plugin) => (
            <Card key={plugin.id} className="flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-semibold tracking-tight">{plugin.name}</h2>
                  <span className="text-xs opacity-50">v{plugin.version}</span>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {plugin.osFamily && (
                  <Badge>
                    <span className="inline-flex items-center gap-1">
                      <MonitorCog className="size-3" />
                      {OS_LABEL[plugin.osFamily]}
                    </span>
                  </Badge>
                )}
                <Badge>
                  <span className="inline-flex items-center gap-1">
                    <Terminal className="size-3" />
                    {plugin.steps.length} paso{plugin.steps.length === 1 ? '' : 's'}
                  </span>
                </Badge>
                {plugin.paramsSchema.length > 0 && (
                  <Badge>
                    <span className="inline-flex items-center gap-1">
                      <ListChecks className="size-3" />
                      {plugin.paramsSchema.length} parámetro
                      {plugin.paramsSchema.length === 1 ? '' : 's'}
                    </span>
                  </Badge>
                )}
              </div>

              {plugin.description && (
                <p className="mt-2 line-clamp-2 text-sm opacity-70">{plugin.description}</p>
              )}

              {plugin.paramsSchema.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {plugin.paramsSchema.map((param) => (
                    <code
                      key={param.name}
                      className="rounded bg-accent/50 px-1.5 py-0.5 text-[11px] font-mono opacity-80"
                    >
                      {param.name}
                      {param.required && (
                        <span className="text-amber-600 dark:text-amber-400">*</span>
                      )}
                    </code>
                  ))}
                </div>
              )}

              <div className="mt-3 flex justify-end gap-1 border-t border-black/5 pt-2 dark:border-white/5">
                <Button
                  variant="ghost"
                  size="icon"
                  title="Clientes habilitados"
                  aria-label="Clientes habilitados"
                  onClick={() => void openClients(plugin)}
                >
                  <Users className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Editar"
                  aria-label="Editar"
                  disabled={!canEdit?.can}
                  onClick={() => setForm(formOf(plugin))}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Borrar"
                  aria-label="Borrar"
                  disabled={!canEdit?.can}
                  className="hover:text-destructive"
                  onClick={() => setDeleting(plugin)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {form && (
        <PluginFormModal
          form={form}
          busy={busy}
          onChange={setForm}
          onCancel={() => setForm(null)}
          onSubmit={() => void submit()}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Borrar plugin"
        confirmLabel="Borrar"
        confirmText={deleting?.name}
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void remove()}
      >
        <p>
          Se borra <strong>{deleting?.name}</strong>. Las ejecuciones pasadas siguen en la bitácora;
          ningún nodo podrá volver a correrlo.
        </p>
      </ConfirmDialog>

      {managingClients && (
        <Modal
          open
          title={`Clientes habilitados — ${managingClients.name}`}
          onClose={() => setManagingClients(null)}
        >
          {enablements === null ? (
            <Loading label="Cargando…" />
          ) : clients.length === 0 ? (
            <EmptyState title="Ningún cliente todavía" />
          ) : (
            <div className="grid gap-3">
              <div className="sticky top-0 z-10 -mx-4 -mt-4 flex items-center justify-between bg-[var(--occ-surface)] px-4 pt-4 pb-3">
                <span className="text-xs opacity-60">
                  {enabledCount} de {clients.length} habilitado{clients.length === 1 ? '' : 's'}
                </span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => void setAllClients(true)}>
                    Habilitar todos
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void setAllClients(false)}>
                    Deshabilitar todos
                  </Button>
                </div>
              </div>
              <div className="grid gap-0.5">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent/40"
                  >
                    <span>{client.name}</span>
                    <Checkbox
                      label=""
                      checked={enablements.get(client.id) ?? false}
                      onChange={(event) => void toggleClient(client.id, event.target.checked)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </section>
  );
}

function PluginFormModal({
  form,
  busy,
  onChange,
  onCancel,
  onSubmit,
}: {
  form: PluginForm;
  busy: boolean;
  onChange: (form: PluginForm) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const patch = (fields: Partial<PluginForm>) => onChange({ ...form, ...fields });

  const duplicateParamNames = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const param of form.paramsSchema) {
      const name = param.name.trim().toUpperCase();
      if (!name) continue;
      if (seen.has(name)) dupes.add(name);
      seen.add(name);
    }
    return dupes;
  }, [form.paramsSchema]);

  const canSubmit =
    form.name.trim() !== '' &&
    form.steps.some((step) => step.script.trim() !== '') &&
    duplicateParamNames.size === 0;

  const moveStep = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= form.steps.length) return;
    const next = [...form.steps];
    const moved = next[index];
    const displaced = next[target];
    if (!moved || !displaced) return;
    next[index] = displaced;
    next[target] = moved;
    patch({ steps: next });
  };

  const availableVars = [
    ...new Set(form.paramsSchema.map((param) => param.name.trim().toUpperCase()).filter(Boolean)),
  ];

  return (
    <Modal
      open
      wide
      title={form.id ? `Editar ${form.name}` : 'Nuevo plugin'}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={busy || !canSubmit}>
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Nombre"
            value={form.name}
            onChange={(event) => patch({ name: event.target.value })}
          />
          <Select
            label="Sistema operativo"
            value={form.osFamily}
            onChange={(event) => patch({ osFamily: event.target.value as OsFamily | '' })}
          >
            <option value="">Cualquiera</option>
            <option value="windows">windows</option>
            <option value="linux">linux</option>
            <option value="darwin">darwin</option>
          </Select>
        </div>

        <Textarea
          label="Descripción"
          rows={2}
          value={form.description}
          onChange={(event) => patch({ description: event.target.value })}
        />

        <section className="grid gap-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Parámetros de entrada</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ paramsSchema: [...form.paramsSchema, emptyParam()] })}
            >
              <Plus className="size-3.5" />
              Parámetro
            </Button>
          </div>
          <p className="text-xs opacity-60">
            Cada parámetro llega al script como una variable de entorno del mismo nombre — sin
            traducción intermedia.
          </p>

          {duplicateParamNames.size > 0 && (
            <Notice tone="warn">
              Nombre repetido: {[...duplicateParamNames].join(', ')}. Cada parámetro necesita un
              nombre distinto.
            </Notice>
          )}

          {form.paramsSchema.map((param, index) => {
            const trimmedName = param.name.trim().toUpperCase();
            const duplicate = trimmedName !== '' && duplicateParamNames.has(trimmedName);
            return (
              <div
                key={index}
                className={`grid gap-2 rounded p-3 ${duplicate ? 'bg-destructive/5 ring-1 ring-destructive/30' : 'bg-accent/30'}`}
              >
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-40 flex-1">
                    <Field
                      label="Nombre (env var)"
                      value={param.name}
                      placeholder="RPNEXT_URL"
                      onChange={(event) =>
                        patch({
                          paramsSchema: replaceAt(form.paramsSchema, index, {
                            ...param,
                            name: event.target.value.toUpperCase(),
                          }),
                        })
                      }
                    />
                  </div>
                  <Select
                    label="Tipo"
                    value={param.type}
                    onChange={(event) =>
                      patch({
                        paramsSchema: replaceAt(form.paramsSchema, index, {
                          ...param,
                          type: event.target.value as PluginParamType,
                        }),
                      })
                    }
                  >
                    <option value="string">texto</option>
                    <option value="number">número</option>
                    <option value="boolean">sí/no</option>
                  </Select>
                  <Checkbox
                    label="Obligatorio"
                    checked={param.required}
                    onChange={(event) =>
                      patch({
                        paramsSchema: replaceAt(form.paramsSchema, index, {
                          ...param,
                          required: event.target.checked,
                        }),
                      })
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto hover:text-destructive"
                    title="Quitar parámetro"
                    aria-label="Quitar parámetro"
                    onClick={() => patch({ paramsSchema: removeAt(form.paramsSchema, index) })}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
                {!param.required && (
                  <Field
                    label="Valor por omisión"
                    value={param.default}
                    onChange={(event) =>
                      patch({
                        paramsSchema: replaceAt(form.paramsSchema, index, {
                          ...param,
                          default: event.target.value,
                        }),
                      })
                    }
                  />
                )}
              </div>
            );
          })}
        </section>

        <section className="grid gap-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Pasos</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ steps: [...form.steps, emptyStep()] })}
            >
              <Plus className="size-3.5" />
              Paso
            </Button>
          </div>

          {availableVars.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 rounded bg-accent/20 px-2.5 py-2 text-xs">
              <span className="opacity-60">Variables disponibles:</span>
              {availableVars.map((name) => (
                <code key={name} className="rounded bg-accent/60 px-1.5 py-0.5 font-mono">
                  {name}
                </code>
              ))}
            </div>
          )}

          {form.steps.map((step, index) => (
            <div key={index} className="grid gap-2 rounded bg-accent/30 p-3">
              <div className="flex flex-wrap items-end gap-2">
                <Badge>Paso {index + 1}</Badge>
                <Select
                  label="Intérprete"
                  value={step.shell}
                  onChange={(event) =>
                    patch({
                      steps: replaceAt(form.steps, index, {
                        ...step,
                        shell: event.target.value as StepForm['shell'],
                      }),
                    })
                  }
                >
                  <option value="powershell">PowerShell</option>
                  <option value="bash">bash</option>
                  <option value="sh">sh</option>
                </Select>
                <Field
                  label="Tiempo máximo (s)"
                  type="number"
                  min={1}
                  max={3600}
                  value={step.timeoutSeconds}
                  onChange={(event) =>
                    patch({
                      steps: replaceAt(form.steps, index, {
                        ...step,
                        timeoutSeconds: Number(event.target.value) || 120,
                      }),
                    })
                  }
                />
                <Checkbox
                  label="Seguir si falla"
                  checked={step.continueOnError}
                  onChange={(event) =>
                    patch({
                      steps: replaceAt(form.steps, index, {
                        ...step,
                        continueOnError: event.target.checked,
                      }),
                    })
                  }
                />
                <div className="ml-auto flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Subir paso"
                    aria-label="Subir paso"
                    disabled={index === 0}
                    onClick={() => moveStep(index, -1)}
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Bajar paso"
                    aria-label="Bajar paso"
                    disabled={index === form.steps.length - 1}
                    onClick={() => moveStep(index, 1)}
                  >
                    <ArrowDown className="size-4" />
                  </Button>
                  {form.steps.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hover:text-destructive"
                      title="Quitar paso"
                      aria-label="Quitar paso"
                      onClick={() => patch({ steps: removeAt(form.steps, index) })}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
              <Textarea
                label={`Script del paso ${index + 1}`}
                rows={4}
                spellCheck={false}
                value={step.script}
                onChange={(event) =>
                  patch({
                    steps: replaceAt(form.steps, index, { ...step, script: event.target.value }),
                  })
                }
              />
            </div>
          ))}
        </section>
      </div>
    </Modal>
  );
}

function replaceAt<T>(list: T[], index: number, value: T): T[] {
  const next = [...list];
  next[index] = value;
  return next;
}

function removeAt<T>(list: T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}
